import { useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { Html, Sparkles } from "@react-three/drei"
import { damp } from "maath/easing"
import * as THREE from "three"

import type { AvatarModel, AvatarPolarity } from "@domo/shared"
import { siliconColor, siliconIntensity } from "./avatarColor"
import { modelPalette } from "./modelPalette"
import { getAvatarMesh } from "./avatarMeshes"
import { MaterializedMesh, type AvatarAnimKey } from "./MaterializedMesh"
import { MeshErrorBoundary } from "./MeshErrorBoundary"

interface AvatarProps {
    siliconIndex: number
    displayName: string
    isLocal?: boolean
    isAnchored?: boolean
    /** Sprint 3 — modelo de encarnación elegido. Si presente, define la
     *  paleta de colores del avatar (core/halo/intensity). Si null usamos
     *  el cálculo legacy siliconColor() basado en el Índice de Silicio. */
    model?: AvatarModel | null
    /** Sprint 5 — polaridad de la firma (alfa/omega). Modula el hue +
     *  intensidad de la paleta y el sentido de rotación de los halos. */
    polarity?: AvatarPolarity | null
    /** Sprint 3 — opacidad de materialización (0..1). Permite que el
     *  componente padre haga fade-in del avatar emergiendo del haz. */
    materializeOpacity?: number
    /** 2026-05-05 — Animación a reproducir si el avatar es rigged.
     *  Si no se pasa, default "idle". */
    animationKey?: AvatarAnimKey
}

// Avatar visual primitivo — usado tanto por el local como por los remotos.
// Geometría: core icosaédrico + halo toroidal rotatorio + esfera de glow + sparkles + nombre.
//
// Estado anchored: el toroide se expande, el sello dorado de la base se
// pronuncia, las sparkles se densifican y el core deja de latir — el
// tripulante se vuelve "intocable" y eso debe leerse a primera vista.
export function Avatar({
    siliconIndex,
    displayName,
    isLocal,
    isAnchored = false,
    model = null,
    polarity = null,
    materializeOpacity = 1,
    animationKey = "idle",
}: AvatarProps) {
    const haloRef = useRef<THREE.Mesh>(null)
    const innerHaloRef = useRef<THREE.Mesh>(null)
    const coreRef = useRef<THREE.Mesh>(null)
    const haloScaleRef = useRef({ s: 1 })

    // Sprint 3/5 — Si el Tripulante eligió firma (modelo + polaridad),
    // la paleta computada gana sobre el cálculo legacy basado en
    // siliconIndex. Si no hay modelo (eg. peers legacy), usamos el
    // siliconColor de Sprint 1.
    const palette = useMemo(
        () => (model ? modelPalette(model, polarity) : null),
        [model, polarity]
    )
    const color = useMemo(
        () => palette?.avatarCore ?? siliconColor(siliconIndex),
        [palette, siliconIndex]
    )
    const haloColor = useMemo(
        () => palette?.avatarHalo ?? color,
        [palette, color]
    )
    const intensity = useMemo(
        () => palette?.emissiveIntensity ?? siliconIntensity(siliconIndex),
        [palette, siliconIndex]
    )

    // Opacidad de materialización: aplicada multiplicativamente a los
    // alphas internos. Se normaliza entre 0 y 1.
    const op = Math.max(0, Math.min(1, materializeOpacity))

    // Sprint 5 — Si esta firma tiene un .glb publicado en R2, lo
    // cargamos y suprimimos el icosaedro emisivo (lo reemplaza la malla
    // 3D real). El halo, baseRing, sparkles y el pointlight siguen
    // alrededor para mantener el lenguaje del Domo.
    const meshDef = useMemo(
        () => getAvatarMesh(model ?? null, polarity ?? null),
        [model, polarity]
    )

    useFrame((state, dt) => {
        const t = state.clock.elapsedTime
        // Sprint 5 — sentido de rotación según polaridad. Alfa horario,
        // omega antihorario. Para legacy (sin polaridad) mantiene horario.
        const dir = palette?.rotationSign ?? 1
        // Halo expand al anclarse: 1 → 1.45.
        damp(haloScaleRef.current, "s", isAnchored ? 1.45 : 1, 0.4, dt)
        if (haloRef.current) {
            haloRef.current.scale.setScalar(haloScaleRef.current.s)
            haloRef.current.rotation.y += dt * (isAnchored ? 0.18 : 0.6) * dir
            haloRef.current.rotation.x =
                Math.sin(t * 0.4) * 0.15 + Math.PI * 0.05
        }
        if (innerHaloRef.current) {
            innerHaloRef.current.rotation.y -= dt * (isAnchored ? 0.12 : 0.4) * dir
            innerHaloRef.current.rotation.z = Math.cos(t * 0.5) * 0.1
        }
        if (coreRef.current) {
            // Anchored: scale fijo (núcleo sellado). Caminando: respira.
            const target = isAnchored ? 1.0 : 1 + Math.sin(t * 1.5) * 0.04
            damp(coreRef.current.scale, "x", target, 0.2, dt)
            damp(coreRef.current.scale, "y", target, 0.2, dt)
            damp(coreRef.current.scale, "z", target, 0.2, dt)
        }
    })

    return (
        <group>
            {/* Core: malla 3D si la firma tiene .glb publicado, si no
                el icosaedro emisivo legacy. El icosaedro también
                aparece como fallback cuando el .glb falla en cargar
                (CORS, 404, archivo corrupto): el MeshErrorBoundary
                captura el throw de useGLTF y nos protege contra
                pantalla negra del Canvas. */}
            {meshDef ? (
                <MeshErrorBoundary
                    label={`Avatar mesh (${model})`}
                    fallback={
                        <mesh
                            ref={coreRef}
                            position={[0, 1, 0]}
                            castShadow
                        >
                            <icosahedronGeometry args={[0.42, 1]} />
                            <meshStandardMaterial
                                color="#000"
                                emissive={color}
                                emissiveIntensity={intensity * op}
                                roughness={0.4}
                                metalness={0.2}
                                transparent={op < 1}
                                opacity={op}
                            />
                        </mesh>
                    }
                >
                    {/* group sin ref — los avatares rigged son humanoides
                        de altura ~1.7m, anclados al piso (y=0). El
                        coreRef se usaba antes para el icosaedro emisivo
                        (que tenía punto de pivote en su centro a y=1);
                        para el rigged ya no aplica. */}
                    <group position={[0, 0, 0]}>
                        <MaterializedMesh
                            def={meshDef}
                            emissiveColor={color}
                            emissiveIntensity={intensity}
                            materializeOpacity={op}
                            animationKey={animationKey}
                        />
                    </group>
                </MeshErrorBoundary>
            ) : (
                <mesh ref={coreRef} position={[0, 1, 0]} castShadow>
                    <icosahedronGeometry args={[0.42, 1]} />
                    <meshStandardMaterial
                        color="#000"
                        emissive={color}
                        emissiveIntensity={intensity * op}
                        roughness={0.4}
                        metalness={0.2}
                        transparent={op < 1}
                        opacity={op}
                    />
                </mesh>
            )}

            {/* Halo de glow esférico — transparente, sin lighting.
                Sprint 6 v2 — renderOrder + depthTest=false: el suelo
                reflector recortaba la esfera del halo según el ángulo
                de cámara porque el reflejo escribía al depth buffer
                antes del render del halo.
                2026-05-05 v2 — Devolvemos el depth test (true por
                default). La esfera está centrada en y=1 con radio 0.7
                → vive siempre arriba del piso (y ≥ 0.3) y el reflector
                no la recorta por geometría. Mantenerla con
                depthTest=false producía un "fantasma" del avatar:
                BlendingMode normal sobre el avatar en pixeles donde
                la esfera proyectada coincidía con el cuerpo, pero la
                esfera ALSO escribía a través del avatar contra el
                fondo — el resultado leía como una réplica translúcida
                del cuerpo a primera vista. Con depth test activo, la
                esfera tinta solo lo que efectivamente está delante
                de ella y la duplicación desaparece. depthWrite sigue
                en false (no dejamos que tape lo que viene después). */}
            <mesh position={[0, 1, 0]} renderOrder={5}>
                <sphereGeometry args={[0.7, 24, 24]} />
                <meshBasicMaterial
                    color={haloColor}
                    transparent
                    opacity={(isAnchored ? 0.28 : 0.18) * op}
                    depthWrite={false}
                />
            </mesh>

            {/* Anillo grande rotatorio (halo principal).
                2026-05-05 — depthTest:false + renderOrder 12. Antes el
                anillo se "cortaba" cuando el horizonte del Domo (suelo
                espejo + arcos lejanos) escribía al depth buffer primero
                y la mitad del anillo que pasaba por detrás de esos
                planos quedaba ocluida. Sin depthTest el anillo siempre
                queda visible alrededor del avatar, sin discontinuidades
                cuando el horizonte está atrás.
                2026-05-05 v2 — `transparent` SIEMPRE true (antes era
                op<1, así que cuando materializeOpacity llegaba a 1 el
                anillo pasaba al pase opaco. En el pase opaco con
                depthWrite=false, el suelo reflector (transparente,
                renderOrder 0) se renderizaba DESPUÉS y sus pixels
                ganaban encima del anillo en la zona enfrente del
                avatar. Forzando transparente, el anillo entra al pase
                transparente con renderOrder 12 y el suelo (renderOrder
                0) se renderiza ANTES — el anillo queda completo. */}
            <mesh ref={haloRef} position={[0, 1, 0]} renderOrder={12}>
                <torusGeometry args={[0.95, 0.04, 16, 64]} />
                <meshStandardMaterial
                    color="#000"
                    emissive={haloColor}
                    emissiveIntensity={intensity * 1.3 * op}
                    roughness={0.2}
                    metalness={0.8}
                    transparent
                    opacity={op}
                    depthWrite={false}
                    depthTest={false}
                />
            </mesh>

            {/* Anillo interior fino — eje opuesto, da ilusión de orbital
                cuántica. Mismo tratamiento de depthTest/renderOrder. */}
            <mesh
                ref={innerHaloRef}
                position={[0, 1, 0]}
                rotation={[Math.PI / 2.5, 0, 0]}
                renderOrder={12}
            >
                <torusGeometry args={[0.7, 0.018, 12, 48]} />
                <meshBasicMaterial
                    color={haloColor}
                    transparent
                    opacity={0.85 * op}
                    depthWrite={false}
                    depthTest={false}
                />
            </mesh>

            {/* 2026-05-05 — Hexágono dorado de la base removido por
                decisión de Zak. El sello "ANCLADO · INTOCABLE" del HUD
                top-left ya comunica el estado anchored sin necesidad
                del refuerzo geométrico debajo del avatar. El hexágono
                de destino (TargetIndicator) sigue apareciendo solo en
                el punto donde el Tripulante eligió ir. */}

            {/* Partículas tenues orbitando — escala con el tier de Silicio. */}
            <Sparkles
                count={isAnchored ? 28 : 14}
                scale={isAnchored ? [2.4, 2.4, 2.4] : [1.8, 1.8, 1.8]}
                position={[0, 1, 0]}
                size={isAnchored ? 4 : 3}
                speed={isAnchored ? 0.18 : 0.35}
                color={haloColor}
                opacity={(isAnchored ? 0.85 : 0.7) * op}
            />

            {/* 2026-05-05 — Punto de luz del core con intensidad
                reducida (era *1.2, ahora *0.45) + distance acotado.
                Antes contribuía al resplandor blanco gigante junto con
                el bloom y el reflector. */}
            <pointLight
                position={[0, 1, 0]}
                intensity={intensity * 0.45 * op}
                distance={3.5}
                color={color}
            />

            {/* Nombre flotante.
                2026-05-05 — Migrado de drei <Text> + <Billboard> a drei
                <Html>. Razón: el <Text> de drei usa troika-three-text,
                que renderea con un shader MSDF sobre un atlas de glifos.
                Cuando MeshReflectorMaterial captura la escena para el
                reflejo del piso, el shader MSDF NO corre en ese pase y
                el atlas crudo (mosaicos amarillos/anaranjados con la
                data SDF) se mostraba reflejado como un patrón ASCII
                fantasma. Pasar a <Html> saca el nombre del WebGL y lo
                renderea como DOM — el reflector no lo captura, el bug
                desaparece, y el texto se ve más nítido a cualquier
                distancia. distanceFactor 8 ajusta el tamaño aparente
                con la cámara para emular la escala del Text 3D
                anterior (fontSize 0.18 a ~6u de cámara ≈ ~14px DOM). */}
            <Html
                position={[0, 2.15, 0]}
                center
                distanceFactor={8}
                zIndexRange={[20, 0]}
                style={{ pointerEvents: "none" }}
            >
                <div
                    style={{
                        color: "#FFFFFF",
                        fontFamily:
                            "'Inter', system-ui, -apple-system, sans-serif",
                        fontSize: 14,
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                        textShadow:
                            "0 0 4px rgba(0,0,0,0.95), 0 0 2px rgba(0,0,0,0.95), 0 1px 1px rgba(0,0,0,0.8)",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        WebkitUserSelect: "none",
                        opacity: op,
                    }}
                >
                    {displayName}
                </div>
            </Html>
        </group>
    )
}
